import React, { useState, useEffect, useMemo } from "react";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Divider, TextField, IconButton, Paper, Card,
  Button, Avatar, CircularProgress, Stack, Chip, Slider, LinearProgress,
  Tabs, Tab, useMediaQuery, useTheme, alpha,
} from "@mui/material";
import {
  CloudUpload, Delete, Edit, Add, Flag, CalendarToday,
  CheckCircle, RadioButtonUnchecked, TimelapseOutlined, WarningAmber,
  EmojiEvents, TrackChanges,
} from "@mui/icons-material";
import axios from "axios";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

const API_URL = "https://backend-global-1vq9.onrender.com/api/goals";

const PRIORITY_COLORS = {
  High:   { bg: C.errorSoft,     color: C.error,     border: alpha(C.error, 0.25) },
  Medium: { bg: C.warningSoft,   color: C.warning,   border: alpha(C.warning, 0.25) },
  Low:    { bg: C.secondarySoft, color: C.secondary, border: alpha(C.secondary, 0.25) },
};

// ── Status helpers ────────────────────────────────────────────────────────────
const computeStatus = (goal) => {
  const progress = Number(goal.progress) || 0;
  const explicit = goal.status;
  if (explicit === "completed" || progress >= 100) return "completed";
  if (goal.dueDate) {
    const due = new Date(goal.dueDate);
    if (!isNaN(due.getTime()) && due.getTime() < Date.now()) return "overdue";
  }
  if (explicit === "in_progress" || progress > 0) return "in_progress";
  return "not_started";
};

const STATUS_CONFIG = {
  not_started: { label: "Not started", color: C.textMuted, bg: C.borderSoft, icon: <RadioButtonUnchecked sx={{ fontSize: 13 }} /> },
  in_progress: { label: "In progress", color: C.primary, bg: C.primarySoft, icon: <TimelapseOutlined sx={{ fontSize: 13 }} /> },
  completed:   { label: "Completed",   color: C.secondary, bg: C.secondarySoft, icon: <CheckCircle sx={{ fontSize: 13 }} /> },
  overdue:     { label: "Overdue",     color: C.error, bg: C.errorSoft, icon: <WarningAmber sx={{ fontSize: 13 }} /> },
};

const formatDueRelative = (dueDate) => {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  if (isNaN(due.getTime())) return null;
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const diff = Math.round((dueDay - today) / dayMs);
  if (diff === 0) return { text: "Due today", urgent: true };
  if (diff === 1) return { text: "Due tomorrow", urgent: true };
  if (diff > 1 && diff <= 7) return { text: `${diff} days left`, urgent: false };
  if (diff > 7) return { text: due.toLocaleDateString("en-KE", { month: "short", day: "numeric" }), urgent: false };
  if (diff === -1) return { text: "Overdue by 1 day", overdue: true };
  return { text: `Overdue by ${Math.abs(diff)} days`, overdue: true };
};

// ── Goal Card ─────────────────────────────────────────────────────────────────
function GoalCard({ goal, onViewDetails, onEdit, onDelete, onProgressChange }) {
  const p = PRIORITY_COLORS[goal.priority] || PRIORITY_COLORS.Medium;
  const status = computeStatus(goal);
  const sc = STATUS_CONFIG[status];
  const progress = Number(goal.progress) || (status === "completed" ? 100 : 0);
  const dueRel = formatDueRelative(goal.dueDate);
  const isCompleted = status === "completed";

  const accent =
    status === "completed" ? C.secondary :
    status === "overdue" ? C.error :
    C.primary;

  return (
    <Card
      sx={{
        position: "relative",
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        bgcolor: isCompleted ? C.secondarySofter : C.paper,
        overflow: "hidden",
        transition: "all 0.2s ease",
        opacity: isCompleted ? 0.92 : 1,
        boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
        "&:hover": {
          boxShadow: "0 6px 20px rgba(13,17,23,0.07)",
          transform: "translateY(-1px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
        },
      }}
    >
      <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            variant="rounded"
            sx={{
              width: 48, height: 48,
              background: isCompleted
                ? `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`
                : `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
              color: "#fff",
              flexShrink: 0,
              borderRadius: 2,
              boxShadow: `0 4px 10px ${(isCompleted ? C.secondary : C.primary)}25`,
            }}
          >
            {isCompleted ? <EmojiEvents sx={{ fontSize: 24 }} /> : <TrackChanges sx={{ fontSize: 24 }} />}
          </Avatar>

          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                fontSize: { xs: 13, sm: 14.5 },
                fontWeight: 700,
                color: C.textPrimary,
                lineHeight: 1.4,
                mb: 0.5,
                textDecoration: isCompleted ? "line-through" : "none",
                textDecorationColor: alpha(C.textMuted, 0.5),
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {goal.title}
            </Typography>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                icon={sc.icon}
                label={sc.label}
                size="small"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: sc.bg, color: sc.color,
                  "& .MuiChip-label": { px: 0.75 },
                  "& .MuiChip-icon": { color: sc.color },
                }}
              />
              <Chip
                icon={<Flag sx={{ fontSize: "12px !important", color: `${p.color} !important` }} />}
                label={goal.priority}
                size="small"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: p.bg, color: p.color,
                  border: `1px solid ${p.border}`,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
              {dueRel && (
                <Chip
                  icon={<CalendarToday sx={{
                    fontSize: "11px !important",
                    color: `${dueRel.overdue ? C.error : dueRel.urgent ? C.warning : C.textSecondary} !important`,
                  }} />}
                  label={dueRel.text}
                  size="small"
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 600,
                    bgcolor: dueRel.overdue ? C.errorSoft : dueRel.urgent ? C.warningSoft : C.borderSoft,
                    color: dueRel.overdue ? C.error : dueRel.urgent ? C.warning : C.textSecondary,
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={0.25}>
            <IconButton size="small" onClick={() => onEdit(goal._id)}
              sx={{ color: C.primary, "&:hover": { bgcolor: C.primarySoft }, borderRadius: 2, p: 0.75 }}>
              <Edit sx={{ fontSize: 17 }} />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(goal._id)}
              sx={{ color: C.error, "&:hover": { bgcolor: C.errorSoft }, borderRadius: 2, p: 0.75 }}>
              <Delete sx={{ fontSize: 17 }} />
            </IconButton>
          </Stack>
        </Stack>

        <Box mt={1.75}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Progress
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: isCompleted ? C.secondary : C.primary }}>
              {progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, progress))}
            sx={{
              height: 6, borderRadius: 3, bgcolor: C.borderSoft,
              "& .MuiLinearProgress-bar": {
                background: isCompleted
                  ? `linear-gradient(90deg, ${C.secondary}, ${C.secondaryLight})`
                  : `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {!isCompleted && onProgressChange && (
          <Box mt={1} px={0.5}>
            <Slider
              size="small"
              value={progress}
              onChange={(_, v) => onProgressChange(goal._id, v)}
              min={0} max={100} step={5}
              sx={{
                color: C.primary, py: 1,
                "& .MuiSlider-thumb": {
                  width: 14, height: 14,
                  boxShadow: `0 2px 6px ${C.primary}40`,
                  "&:hover": { boxShadow: `0 0 0 6px ${alpha(C.primary, 0.12)}` },
                },
                "& .MuiSlider-rail": { bgcolor: C.borderSoft, opacity: 1 },
                "& .MuiSlider-track": {
                  background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
                  border: "none",
                },
              }}
            />
          </Box>
        )}

        <Button
          fullWidth
          onClick={() => onViewDetails(goal._id)}
          sx={{
            mt: 1,
            bgcolor: isCompleted ? C.secondary : C.primary,
            color: "#fff",
            borderRadius: 2,
            fontSize: 12, fontWeight: 700,
            textTransform: "none", py: 0.85,
            boxShadow: "none",
            "&:hover": {
              bgcolor: isCompleted ? C.secondaryDark : C.primaryDark,
              boxShadow: `0 4px 12px ${(isCompleted ? C.secondary : C.primary)}30`,
            },
          }}
        >
          View Details
        </Button>
      </Box>
    </Card>
  );
}

// ── Create / Edit Form ────────────────────────────────────────────────────────
function GoalForm({
  title, setTitle, priority, setPriority, dueDate, setDueDate,
  note, setNote, picture, setPicture, progress, setProgress,
  currentImageUrl, editingGoalId, onSubmit, onCancel,
}) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 4, height: 22, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, borderRadius: 1 }} />
          <Typography sx={{ color: C.textPrimary, fontSize: 15, fontWeight: 700 }}>
            {editingGoalId ? "Edit Goal" : "Create Goal"}
          </Typography>
        </Stack>
        {editingGoalId && onCancel && (
          <Button size="small" onClick={onCancel} sx={{ textTransform: "none", fontSize: 12, color: C.textMuted }}>
            Cancel
          </Button>
        )}
      </Stack>
      <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />

      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75, color: C.textSecondary }}>
        Add Image
      </Typography>
      <Paper
        elevation={0}
        sx={{
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          bgcolor: C.primarySofter,
          borderRadius: 2.5,
          border: `1.5px dashed ${alpha(C.primary, 0.4)}`,
          py: 3, mb: 2.5, cursor: "pointer", gap: 0.75,
          transition: "all 0.2s",
          "&:hover": { borderColor: C.primary, bgcolor: C.primarySoft },
        }}
        onClick={() => document.getElementById("upload-input").click()}
      >
        <CloudUpload sx={{ fontSize: 28, color: C.primary }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.primary }}>
          {picture ? picture.name : "Click to upload image"}
        </Typography>
        <Typography sx={{ fontSize: 10, color: alpha(C.primary, 0.7) }}>PNG, JPG supported</Typography>
        <input
          id="upload-input"
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => setPicture(e.target.files[0])}
        />
        {currentImageUrl && !picture && (
          <img
            src={currentImageUrl}
            alt="Current goal"
            style={{ width: 100, marginTop: 10, borderRadius: 8, objectFit: "cover", border: `1px solid ${C.border}` }}
          />
        )}
      </Paper>

      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75, color: C.textSecondary }}>Title *</Typography>
      <TextField
        fullWidth placeholder="What do you want to achieve?"
        value={title} onChange={(e) => setTitle(e.target.value)}
        variant="outlined" size="small"
        sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 13, "&.Mui-focused fieldset": { borderColor: C.primary } } }}
      />

      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75, color: C.textSecondary }}>Priority</Typography>
      <TextField
        select fullWidth value={priority}
        onChange={(e) => setPriority(e.target.value)}
        SelectProps={{ native: true }}
        variant="outlined" size="small"
        sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 13, "&.Mui-focused fieldset": { borderColor: C.primary } } }}
      >
        <option value="High">High</option>
        <option value="Medium">Medium</option>
        <option value="Low">Low</option>
      </TextField>

      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75, color: C.textSecondary }}>Due Date</Typography>
      <TextField
        type="date" fullWidth value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
        variant="outlined" size="small"
        sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 13, "&.Mui-focused fieldset": { borderColor: C.primary } } }}
      />

      {editingGoalId && setProgress && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.textSecondary }}>Progress</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.primary }}>{progress}%</Typography>
          </Stack>
          <Box px={0.5} mb={2}>
            <Slider
              value={progress}
              onChange={(_, v) => setProgress(v)}
              min={0} max={100} step={5}
              marks={[{ value: 0 }, { value: 50 }, { value: 100 }]}
              sx={{
                color: C.primary,
                "& .MuiSlider-thumb": { width: 16, height: 16, boxShadow: `0 2px 6px ${C.primary}40` },
                "& .MuiSlider-rail": { bgcolor: C.borderSoft, opacity: 1 },
                "& .MuiSlider-track": { background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`, border: "none" },
              }}
            />
          </Box>
        </>
      )}

      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75, color: C.textSecondary }}>Note</Typography>
      <TextField
        fullWidth placeholder="Why is this important to you?"
        multiline rows={3} value={note}
        onChange={(e) => setNote(e.target.value)}
        variant="outlined" size="small"
        sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 13, "&.Mui-focused fieldset": { borderColor: C.primary } } }}
      />

      <Button
        fullWidth onClick={onSubmit}
        sx={{
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
          color: "#fff", borderRadius: 2.5, py: 1.4,
          textTransform: "none", fontWeight: 700, fontSize: 14,
          boxShadow: "none",
          "&:hover": {
            background: `linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%)`,
            boxShadow: `0 6px 18px ${C.primary}40`,
          },
        }}
      >
        {editingGoalId ? "Update Goal" : "Create Goal"}
      </Button>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MyGoals() {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [picture, setPicture] = useState(null);
  const [progress, setProgress] = useState(0);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [showFormMobile, setShowFormMobile] = useState(false);
  const [filterTab, setFilterTab] = useState(0);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id || user?._id;

  useEffect(() => { if (userId) fetchGoals(); }, [userId]);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/user/${userId}`);
      setGoals(res.data);
    } catch (err) {
      console.error("Error fetching goals:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingGoalId(null);
    setTitle(""); setPriority("Medium");
    setDueDate(""); setNote("");
    setPicture(null); setCurrentImageUrl(null);
    setProgress(0);
    if (isMobile) setShowFormMobile(false);
  };

  const handleCreateGoal = async () => {
    if (!title.trim()) { alert("Title is required"); return; }
    try {
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("title", title);
      formData.append("priority", priority);
      formData.append("progress", 0);
      formData.append("status", "not_started");
      if (dueDate) formData.append("dueDate", dueDate);
      if (picture) formData.append("picture", picture);
      // ✅ Backend model uses `note` (single field)
      if (note) formData.append("note", note);
      await axios.post(API_URL, formData, { headers: { "Content-Type": "multipart/form-data" } });
      resetForm();
      fetchGoals();
    } catch (err) {
      console.error("Error creating goal:", err);
      alert("Error creating goal");
    }
  };

  const handleUpdateGoal = async (goalId) => {
    try {
      const res = await axios.get(`${API_URL}/${goalId}`);
      const goal = res.data;
      setEditingGoalId(goalId);
      setTitle(goal.title);
      setPriority(goal.priority);
      setDueDate(goal.dueDate ? goal.dueDate.split("T")[0] : "");
      setNote(goal.note || "");
      setProgress(Number(goal.progress) || 0);
      setCurrentImageUrl(goal.picture || null);
      setPicture(null);
      if (isMobile) setShowFormMobile(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Error loading goal:", err);
      alert("Failed to load goal for edit");
    }
  };

  const handleSubmitUpdate = async () => {
    if (!editingGoalId) return;
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("priority", priority);
      formData.append("progress", progress);
      formData.append("status", progress >= 100 ? "completed" : progress > 0 ? "in_progress" : "not_started");
      if (dueDate) formData.append("dueDate", dueDate);
      // ✅ Always send note (even empty) so users can clear it
      formData.append("note", note || "");
      if (picture) formData.append("picture", picture);
      await axios.put(`${API_URL}/${editingGoalId}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      resetForm();
      fetchGoals();
    } catch (err) {
      console.error("Error updating goal:", err);
      alert("Failed to update goal");
    }
  };

  const handleInlineProgress = async (goalId, newProgress) => {
    setGoals((prev) => prev.map((g) =>
      g._id === goalId
        ? { ...g, progress: newProgress, status: newProgress >= 100 ? "completed" : newProgress > 0 ? "in_progress" : "not_started" }
        : g
    ));
    try {
      const formData = new FormData();
      formData.append("progress", newProgress);
      formData.append("status", newProgress >= 100 ? "completed" : newProgress > 0 ? "in_progress" : "not_started");
      await axios.put(`${API_URL}/${goalId}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (err) {
      console.error("Error updating progress:", err);
      fetchGoals();
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm("Are you sure you want to delete this goal?")) return;
    try {
      await axios.delete(`${API_URL}/${goalId}`);
      fetchGoals();
    } catch (err) {
      console.error("Error deleting goal:", err);
      alert("Error deleting goal");
    }
  };

  const priorityRank = { High: 0, Medium: 1, Low: 2 };

  const sortedGoals = useMemo(() => {
    return [...goals].sort((a, b) => {
      const sa = computeStatus(a);
      const sb = computeStatus(b);
      if (sa === "completed" && sb !== "completed") return 1;
      if (sb === "completed" && sa !== "completed") return -1;
      if (sa === "overdue" && sb !== "overdue") return -1;
      if (sb === "overdue" && sa !== "overdue") return 1;
      const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      if (da !== db) return da - db;
      return (priorityRank[a.priority] ?? 99) - (priorityRank[b.priority] ?? 99);
    });
  }, [goals]);

  const filteredGoals = useMemo(() => {
    if (filterTab === 0) return sortedGoals;
    if (filterTab === 1) return sortedGoals.filter((g) => {
      const s = computeStatus(g);
      return s === "not_started" || s === "in_progress";
    });
    if (filterTab === 2) return sortedGoals.filter((g) => computeStatus(g) === "completed");
    if (filterTab === 3) return sortedGoals.filter((g) => computeStatus(g) === "overdue");
    return sortedGoals;
  }, [sortedGoals, filterTab]);

  const counts = useMemo(() => {
    const c = { total: goals.length, active: 0, completed: 0, overdue: 0 };
    goals.forEach((g) => {
      const s = computeStatus(g);
      if (s === "completed") c.completed += 1;
      else if (s === "overdue") c.overdue += 1;
      else c.active += 1;
    });
    return c;
  }, [goals]);

  const completionRate = goals.length === 0 ? 0 : Math.round((counts.completed / goals.length) * 100);

  useEffect(() => { setPage(0); }, [filterTab]);

  const goalsPerPage = 4;
  const startIndex = page * goalsPerPage;
  const displayedGoals = filteredGoals.slice(startIndex, startIndex + goalsPerPage);
  const totalPages = Math.ceil(filteredGoals.length / goalsPerPage);

  const formProps = {
    title, setTitle, priority, setPriority, dueDate, setDueDate,
    note, setNote, picture, setPicture, progress, setProgress,
    currentImageUrl, editingGoalId,
    onSubmit: editingGoalId ? handleSubmitUpdate : handleCreateGoal,
    onCancel: resetForm,
  };

  const StatTile = ({ label, value, accent, accentDark, icon }) => (
    <Paper
      elevation={0}
      sx={{
        position: "relative", flex: 1, minWidth: 0,
        p: { xs: 1.75, sm: 2 }, borderRadius: 2.5,
        background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
        color: "#fff", overflow: "hidden",
        transition: "all 0.2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 20px ${accent}40` },
        "&::after": {
          content: '""', position: "absolute",
          top: "-30%", right: "-15%",
          width: 90, height: 90, borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: 1.5,
          bgcolor: "rgba(255,255,255,0.18)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box minWidth={0}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "rgba(255,255,255,0.85)" }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 5, lg: 10 }, py: { xs: 3, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            position: "relative", borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 }, mb: { xs: 2.5, sm: 3 },
            color: "#fff", boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute", top: "-40%", right: "-10%",
            width: { xs: 180, sm: 260 }, height: { xs: 180, sm: 260 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box>
                <Typography sx={{
                  fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                  textTransform: "uppercase", color: "rgba(255,255,255,0.7)", mb: 0.75,
                }}>
                  Personal Goals
                </Typography>
                <Typography sx={{
                  fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
                  color: "#fff", lineHeight: 1.15, letterSpacing: -0.5,
                }}>
                  My Goals
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: { xs: 13, sm: 14 }, mt: 0.5 }}>
                  Track progress, hit milestones, build momentum
                </Typography>
              </Box>

              {isMobile && !showFormMobile && (
                <Button
                  variant="contained" startIcon={<Add />}
                  onClick={() => { resetForm(); setShowFormMobile(true); }}
                  sx={{
                    bgcolor: "#fff", color: C.primary,
                    borderRadius: 2.5, fontWeight: 700, fontSize: 13,
                    textTransform: "none", boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  New Goal
                </Button>
              )}
            </Stack>

            {goals.length > 0 && (
              <Box sx={{ mt: 2.5, maxWidth: 480 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>
                    Overall completion
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>
                    {completionRate}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate" value={completionRate}
                  sx={{
                    height: 8, borderRadius: 4,
                    bgcolor: "rgba(255,255,255,0.15)",
                    "& .MuiLinearProgress-bar": {
                      background: `linear-gradient(90deg, ${C.secondaryLight}, #fff)`,
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Paper>

        {goals.length > 0 && (
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2 }}
            mb={{ xs: 2.5, sm: 3 }}
            sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, gap: { xs: 1, sm: 0 } }}
          >
            <StatTile label="Total" value={counts.total} accent={C.primary} accentDark={C.primaryDark}
              icon={<TrackChanges sx={{ fontSize: 18, color: "#fff" }} />} />
            <StatTile label="Active" value={counts.active} accent={C.primaryLight} accentDark={C.primary}
              icon={<TimelapseOutlined sx={{ fontSize: 18, color: "#fff" }} />} />
            <StatTile label="Done" value={counts.completed} accent={C.secondary} accentDark={C.secondaryDark}
              icon={<EmojiEvents sx={{ fontSize: 18, color: "#fff" }} />} />
            {counts.overdue > 0 && (
              <StatTile label="Overdue" value={counts.overdue} accent={C.error} accentDark="#991B1B"
                icon={<WarningAmber sx={{ fontSize: 18, color: "#fff" }} />} />
            )}
          </Stack>
        )}

        {isMobile && showFormMobile && (
          <Box sx={{
            bgcolor: C.paper, borderRadius: 3,
            border: `1px solid ${C.border}`,
            p: 2.5, mb: 3,
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
          }}>
            <GoalForm {...formProps} />
            {!editingGoalId && (
              <Button fullWidth size="small" onClick={() => setShowFormMobile(false)}
                sx={{ mt: 1.5, textTransform: "none", fontSize: 12, color: C.textMuted }}>
                Cancel
              </Button>
            )}
          </Box>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
          <Box flex={1} minWidth={0}>
            {goals.length > 0 && (
              <Paper elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${C.border}`, bgcolor: C.paper, mb: 2, overflow: "hidden" }}>
                <Tabs
                  value={filterTab} onChange={(_, v) => setFilterTab(v)}
                  variant="scrollable" scrollButtons={false}
                  sx={{
                    minHeight: 44,
                    "& .MuiTab-root": {
                      textTransform: "none", fontWeight: 700, fontSize: 12.5,
                      minHeight: 44, color: C.textSecondary,
                      "&.Mui-selected": { color: C.primary },
                    },
                    "& .MuiTabs-indicator": { bgcolor: C.primary, height: 3 },
                  }}
                >
                  <Tab label={`All (${counts.total})`} />
                  <Tab label={`Active (${counts.active})`} />
                  <Tab label={`Completed (${counts.completed})`} />
                  <Tab label={`Overdue (${counts.overdue})`} disabled={counts.overdue === 0} />
                </Tabs>
              </Paper>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress size={44} thickness={5} sx={{ color: C.primary }} />
              </Box>
            ) : displayedGoals.length === 0 ? (
              <Box
                display="flex" flexDirection="column" alignItems="center"
                justifyContent="center" minHeight={200}
                bgcolor={C.paper} borderRadius={3}
                border={`1px solid ${C.border}`} py={5}
              >
                <TrackChanges sx={{ fontSize: 44, color: C.textMuted, opacity: 0.5, mb: 1 }} />
                <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: 14, mb: 0.5 }}>
                  {goals.length === 0 ? "No goals yet" : "Nothing in this filter"}
                </Typography>
                <Typography sx={{ color: C.textSecondary, fontSize: 12 }}>
                  {goals.length === 0
                    ? "Create your first goal to start tracking"
                    : "Try a different filter or create a new goal"}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {displayedGoals.map((goal) => (
                  <GoalCard
                    key={goal._id}
                    goal={goal}
                    onViewDetails={() => navigate(`/goal/${goal._id}`)}
                    onEdit={handleUpdateGoal}
                    onDelete={handleDeleteGoal}
                    onProgressChange={handleInlineProgress}
                  />
                ))}
              </Stack>
            )}

            {totalPages > 1 && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" mt={3} flexWrap="wrap" gap={2}>
                <Button variant="outlined" onClick={() => setPage((p) => p - 1)} disabled={page === 0}
                  sx={{
                    borderRadius: 2.5, borderColor: C.primary, color: C.primary,
                    fontWeight: 700, fontSize: 13, textTransform: "none", px: 2.5,
                    "&:hover": { borderColor: C.primaryDark, color: C.primaryDark, bgcolor: C.primarySoft },
                    "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted },
                  }}
                >
                  ← Previous
                </Button>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Box key={i} onClick={() => setPage(i)}
                      sx={{
                        width: i === page ? 24 : 8, height: 8,
                        borderRadius: 4,
                        bgcolor: i === page ? C.primary : alpha(C.primary, 0.2),
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    />
                  ))}
                </Stack>
                <Button variant="outlined" onClick={() => setPage((p) => p + 1)}
                  disabled={startIndex + goalsPerPage >= filteredGoals.length}
                  sx={{
                    borderRadius: 2.5, borderColor: C.primary, color: C.primary,
                    fontWeight: 700, fontSize: 13, textTransform: "none", px: 2.5,
                    "&:hover": { borderColor: C.primaryDark, color: C.primaryDark, bgcolor: C.primarySoft },
                    "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted },
                  }}
                >
                  Next →
                </Button>
              </Stack>
            )}
          </Box>

          {!isMobile && (
            <Box sx={{
              position: "relative",
              width: 360, flexShrink: 0,
              bgcolor: C.paper, borderRadius: 3,
              border: `1px solid ${C.border}`,
              p: 3,
              position: "sticky", top: 24,
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "3px",
                background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`,
              },
            }}>
              <GoalForm {...formProps} />
            </Box>
          )}
        </Stack>
      </Box>

      <Footer />
    </Box>
  );
}